import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Cpu, Plus, PencilSimple, Trash } from "@phosphor-icons/react";

const EMPTY_IMG = "https://images.pexels.com/photos/34207359/pexels-photo-34207359.jpeg";

const STATUS = {
  working: { color: "#00E676", label: "WORKING" },
  broken: { color: "#FF3B30", label: "BROKEN" },
  testing: { color: "#FFB000", label: "TESTING" },
};

const blank = {
  name: "",
  type: "",
  quantity: 1,
  status: "working",
  notes: "",
  pin_port: "",
  microcontroller: "",
};

export default function InventoryTable() {
  const [parts, setParts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    try {
      const res = await api.get("/parts");
      setParts(res.data);
    } catch (e) {
      /* ignore */
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({ ...blank, ...p });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    const payload = {
      name: form.name,
      type: form.type,
      quantity: Number(form.quantity) || 1,
      status: form.status,
      notes: form.notes,
      pin_port: form.pin_port,
      microcontroller: form.microcontroller,
    };
    try {
      if (editing) {
        const res = await api.put(`/parts/${editing.id}`, payload);
        setParts((prev) => prev.map((p) => (p.id === editing.id ? res.data : p)));
        toast.success("Part updated");
      } else {
        const res = await api.post("/parts", payload);
        setParts((prev) => [res.data, ...prev]);
        toast.success("Part logged");
      }
      setOpen(false);
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/parts/${id}`);
      setParts((prev) => prev.filter((p) => p.id !== id));
      toast.success("Part removed");
    } catch (e) {
      /* ignore */
    }
  };

  const field = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const inputCls =
    "w-full bg-[#0A0B0E] border border-[#272A35] px-3 py-2 text-sm font-mono focus:outline-none focus:border-[#FF5A00] transition-colors";
  const labelCls = "text-[10px] uppercase tracking-widest text-[#94A3B8] font-bold";

  return (
    <div className="border border-[#272A35] bg-[#111318]" data-testid="inventory-table">
      <div className="border-b border-[#272A35] px-4 py-3 flex items-center justify-between">
        <span className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#94A3B8] flex items-center gap-2">
          <Cpu size={16} weight="bold" className="text-[#FF5A00]" /> Component Inventory
          <span className="text-[#475569]">[{parts.length}]</span>
        </span>
        <button
          data-testid="add-part-btn"
          onClick={openNew}
          className="text-xs uppercase tracking-widest font-bold px-3 py-1.5 bg-[#FF5A00] text-[#0A0B0E] border border-[#FF5A00] hover:bg-transparent hover:text-[#FF5A00] transition-colors active:scale-95 flex items-center gap-1.5"
        >
          <Plus size={14} weight="bold" /> Log Part
        </button>
      </div>

      {parts.length === 0 ? (
        <div className="relative p-12 grid place-items-center overflow-hidden">
          <img src={EMPTY_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10 grayscale" />
          <div className="relative text-center">
            <Cpu size={40} weight="bold" className="text-[#272A35] mx-auto mb-3" />
            <p className="text-sm text-[#94A3B8]">No components logged.</p>
            <p className="text-xs text-[#475569]">Track parts, wiring pins &amp; controllers here.</p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-[#475569]">
                <th className="px-4 py-3 font-bold">Component</th>
                <th className="px-4 py-3 font-bold">Type</th>
                <th className="px-4 py-3 font-bold">Qty</th>
                <th className="px-4 py-3 font-bold">Status</th>
                <th className="px-4 py-3 font-bold">Pin / Port</th>
                <th className="px-4 py-3 font-bold">Controller</th>
                <th className="px-4 py-3 font-bold">Notes</th>
                <th className="px-4 py-3 font-bold text-right">Ops</th>
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {parts.map((p) => {
                const st = STATUS[p.status] || STATUS.working;
                return (
                  <tr key={p.id} className="border-t border-[#272A35] hover:bg-[#1A1D24] transition-colors" data-testid={`part-row-${p.id}`}>
                    <td className="px-4 py-3 font-bold text-[#F8FAFC]">{p.name}</td>
                    <td className="px-4 py-3 text-[#94A3B8]">{p.type || "—"}</td>
                    <td className="px-4 py-3 text-[#94A3B8]">{p.quantity}</td>
                    <td className="px-4 py-3">
                      <span className="flex items-center gap-2 text-xs uppercase" style={{ color: st.color }}>
                        <span className="w-2 h-2 rounded-full" style={{ background: st.color }} />
                        {st.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[#94A3B8]">{p.pin_port || "—"}</td>
                    <td className="px-4 py-3 text-[#94A3B8]">{p.microcontroller || "—"}</td>
                    <td className="px-4 py-3 text-[#475569] max-w-[220px] truncate">{p.notes || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button data-testid={`edit-part-${p.id}`} onClick={() => openEdit(p)} className="text-[#94A3B8] hover:text-[#FF5A00]">
                          <PencilSimple size={15} weight="bold" />
                        </button>
                        <button data-testid={`delete-part-${p.id}`} onClick={() => remove(p.id)} className="text-[#94A3B8] hover:text-[#FF3B30]">
                          <Trash size={15} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#111318] border border-[#272A35] rounded-none text-[#F8FAFC] font-mono max-w-lg" data-testid="part-dialog">
          <DialogHeader>
            <DialogTitle className="font-head uppercase tracking-tight">{editing ? "Edit Component" : "Log New Component"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div className="col-span-2">
              <label className={labelCls}>Name *</label>
              <input data-testid="part-name-input" value={form.name} onChange={(e) => field("name", e.target.value)} className={inputCls} placeholder="e.g. HC-SR04 Ultrasonic" />
            </div>
            <div>
              <label className={labelCls}>Type</label>
              <input data-testid="part-type-input" value={form.type} onChange={(e) => field("type", e.target.value)} className={inputCls} placeholder="Sensor / Motor" />
            </div>
            <div>
              <label className={labelCls}>Quantity</label>
              <input data-testid="part-qty-input" type="number" min="0" value={form.quantity} onChange={(e) => field("quantity", e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Status</label>
              <select data-testid="part-status-select" value={form.status} onChange={(e) => field("status", e.target.value)} className={inputCls}>
                <option value="working">Working</option>
                <option value="broken">Broken</option>
                <option value="testing">Testing</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Pin / Port</label>
              <input data-testid="part-pin-input" value={form.pin_port} onChange={(e) => field("pin_port", e.target.value)} className={inputCls} placeholder="D7 / A0 / GPIO14" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Microcontroller</label>
              <input data-testid="part-mcu-input" value={form.microcontroller} onChange={(e) => field("microcontroller", e.target.value)} className={inputCls} placeholder="Arduino Uno / ESP32 / Raspberry Pi" />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Notes</label>
              <textarea data-testid="part-notes-input" value={form.notes} onChange={(e) => field("notes", e.target.value)} rows={2} className={inputCls} placeholder="Wiring notes, quirks…" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2 text-xs uppercase tracking-widest border border-[#272A35] hover:bg-[#1A1D24]">
              Cancel
            </button>
            <button
              data-testid="save-part-btn"
              onClick={save}
              className="px-4 py-2 text-xs uppercase tracking-widest font-bold bg-[#FF5A00] text-[#0A0B0E] border border-[#FF5A00] hover:bg-transparent hover:text-[#FF5A00] transition-colors active:scale-95"
            >
              {editing ? "Update" : "Log Component"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
